import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';

interface Option {
  id: number;
  label: string;
}

interface MultiSelectDropdownProps {
  options: Option[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  placeholder?: string;
  className?: string;
}

export default function MultiSelectDropdown({
  options,
  selectedIds,
  onChange,
  placeholder = 'Select…',
  className = '',
}: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Recalculate panel position whenever it opens
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPanelStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
    });
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node) &&
        !(e.target as Element).closest('[data-msd-panel]')
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (id: number) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter(x => x !== id)
        : [...selectedIds, id],
    );
  };

  const label =
    selectedIds.length === 0
      ? placeholder
      : options
          .filter(o => selectedIds.includes(o.id))
          .map(o => o.label)
          .join(', ');

  const panel = open ? (
    createPortal(
      <div
        data-msd-panel
        style={panelStyle}
        className="rounded-lg border border-dark-border bg-dark-card shadow-xl overflow-hidden max-h-64 overflow-y-auto"
      >
        {options.map(o => {
          const selected = selectedIds.includes(o.id);
          return (
            <button
              key={o.id}
              type="button"
              onMouseDown={e => e.preventDefault()}
              onClick={() => toggle(o.id)}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-dark-hover transition-colors"
            >
              <span
                className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center ${
                  selected
                    ? 'bg-primary-600 border-primary-500'
                    : 'border-dark-border bg-transparent'
                }`}
              >
                {selected && <CheckIcon className="h-3 w-3 text-white" />}
              </span>
              <span className={selected ? 'text-white' : 'text-gray-300'}>{o.label}</span>
            </button>
          );
        })}
      </div>,
      document.body,
    )
  ) : null;

  return (
    <div className={`relative ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between gap-2 bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-left text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
      >
        <span className={`truncate ${selectedIds.length === 0 ? 'text-dark-muted' : ''}`}>
          {label}
        </span>
        <ChevronDownIcon
          className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {panel}
    </div>
  );
}
